import { useCallback, useEffect, type SetStateAction } from "react"
import type { JsonValue } from "type-fest"
import {
  UserPreferenceDocument,
  UserPreferenceQuery,
  UserPreferenceQueryVariables,
  useUpsertUserPreferenceMutation,
  useUserPreferenceQuery
} from "@pathwright/graphql-client/src/generated"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import { useSkipAuthRequiredQuery } from "../utils/useAuthAwareQuery"

export type UserPreferenceOptions = {
  key: string
  scope?: string
  // Skip queries and saves until the consumer is enabled.
  enabled?: boolean
}

// Temporary cache IDs for preferences that haven't been created on the server.
let nextOptimisticId = -1

// Read and optimistically save a status through the generated Apollo hooks.
export function useUserPreference<T extends JsonValue = JsonValue>({
  key,
  scope = "",
  enabled = true
}: UserPreferenceOptions) {
  const { me, school } = usePathwrightContext()
  const skipAuthRequiredQuery = useSkipAuthRequiredQuery()
  // The token may be gone before the signed-in user context updates.
  const available =
    enabled && !skipAuthRequiredQuery && !!me?.id && !!school?.id

  const query = useUserPreferenceQuery({
    variables: { key, scope },
    skip: !available
  })

  const preference = query.data?.userPreference
  const status = available
    ? (preference?.data as { status: T | null } | undefined)?.status
    : undefined

  const [upsert, upsertState] = useUpsertUserPreferenceMutation({
    update(cache, { data }) {
      if (!data?.upsertUserPreference) return
      // A first save creates the record, but Apollo's lookup may still say null.
      // Replace that query result with the saved record so readers update too.
      cache.writeQuery<UserPreferenceQuery, UserPreferenceQueryVariables>({
        query: UserPreferenceDocument,
        variables: { key, scope },
        data: { userPreference: data.upsertUserPreference }
      })
    }
  })

  // Mutation state belongs to this preference, not a previously rendered scope.
  useEffect(() => {
    upsertState.reset()
  }, [key, scope, me?.id, school?.id, upsertState.reset])

  const setStatus = useCallback(
    (update: SetStateAction<T | null | undefined>) => {
      if (!available) return

      // Support both function and value updates
      const nextStatus = typeof update === "function" ? update(status) : update
      if (nextStatus === undefined) return

      const now = new Date().toISOString()
      // setStatus(null) saves data: { status: null }. This resets the status without deleting the UserPreference.
      // Catch the rejected promise and do nothing. Instead, rely on the returned upsertState.error.
      void upsert({
        variables: { key, scope, data: { status: nextStatus } },
        // Apollo runs our cache update immediately, then replaces this response
        // with the server result or rolls it back if the mutation fails.
        optimisticResponse: {
          __typename: "Mutations",
          upsertUserPreference: {
            __typename: "UserPreference",
            id: preference?.id ?? nextOptimisticId--,
            user_id: me!.id,
            school_id: school!.id,
            key,
            scope,
            data: { status: nextStatus },
            created: preference?.created ?? now,
            modified: now
          }
        }
      }).catch(() => {})
    },
    [available, key, scope, status, preference, me?.id, school?.id, upsert]
  )

  return { status, setStatus, query, upsertState }
}

export default useUserPreference
